import React from "react";
import { motion } from "framer-motion";
import {
  HeartIcon as HeartOutlineIcon,
  PlayIcon,
  SparklesIcon,
} from "@heroicons/react/24/outline";
import { HeartIcon as HeartSolidIcon } from "@heroicons/react/24/solid";

interface PatreonCreatorCardProps {
  creator: {
    name: string;
    category: string;
    description: string;
    avatar?: string;
    supporters: number;
    gradient: string;
    isLiked?: boolean;
  };
  onLike?: (creatorId: string) => void;
  onBecomePatron?: (creatorId: string) => void;
  onCreatorClick?: (creatorId: string) => void;
  variant?: "default" | "compact" | "featured";
}

const PatreonCreatorCard: React.FC<PatreonCreatorCardProps> = ({
  creator,
  onLike,
  onBecomePatron,
  onCreatorClick,
  variant = "default",
}) => {
  const [isHovered, setIsHovered] = React.useState(false);
  const [isLiked, setIsLiked] = React.useState(creator.isLiked || false);

  const handleLike = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsLiked(!isLiked);
    onLike?.(creator.name);
  };

  const handleBecomePatron = (e: React.MouseEvent) => {
    e.stopPropagation();
    onBecomePatron?.(creator.name);
  };

  const handleCardClick = () => {
    onCreatorClick?.(creator.name);
  };

  const formatSupporters = (count: number) => {
    if (count >= 1000000) {
      return `${(count / 1000000).toFixed(1)}M`;
    } else if (count >= 1000) {
      return `${(count / 1000).toFixed(1)}K`;
    }
    return count.toString();
  };

  if (variant === "compact") {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="flex items-center p-patreon-4 bg-patreon-white rounded-patreon-xl border border-patreon-gray-lighter hover:border-patreon-orange hover:shadow-patreon-md transition-all duration-patreon-normal cursor-pointer group"
        onClick={handleCardClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className="w-12 h-12 bg-gradient-to-r from-patreon-orange to-patreon-pink rounded-full mr-patreon-3 flex-shrink-0">
          <img
            src={creator.avatar || "/api/placeholder/48/48"}
            alt={creator.name}
            className="w-full h-full rounded-full object-cover"
          />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-patreon-semibold text-patreon-black text-patreon-sm truncate group-hover:text-patreon-orange transition-colors duration-patreon-fast">
            {creator.name}
          </h3>
          <p className="text-patreon-gray text-patreon-xs">{formatSupporters(creator.supporters)} supporters</p>
        </div>
        <div className="flex items-center space-x-patreon-2">
          <button
            onClick={handleLike}
            className="p-patreon-2 text-patreon-gray hover:text-patreon-orange transition-colors duration-patreon-fast"
          >
            {isLiked ? (
              <HeartSolidIcon className="w-5 h-5 text-patreon-orange" />
            ) : (
              <HeartOutlineIcon className="w-5 h-5" />
            )}
          </button>
          <div className="w-px h-patreon-6 bg-patreon-gray-lighter"></div>
          <button className="px-patreon-3 py-patreon-1 bg-patreon-orange text-patreon-white text-patreon-xs font-patreon-medium rounded-patreon-md hover:bg-patreon-orange-dark transition-colors duration-patreon-fast">
            Support
          </button>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      whileHover={{ y: -4 }}
      className="bg-patreon-white rounded-patreon-2xl shadow-patreon-base hover:shadow-patreon-xl transition-all duration-patreon-normal overflow-hidden group cursor-pointer"
      onClick={handleCardClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Header with gradient background */}
      <div className={`h-patreon-32 bg-gradient-to-r ${creator.gradient} relative overflow-hidden`}>
        <div className="absolute inset-0 bg-black/10"></div>

        {/* Floating elements */}
        <div className="absolute top-patreon-4 right-patreon-4 opacity-20">
          <div className="w-16 h-16 bg-patreon-white/20 rounded-full blur-xl"></div>
        </div>
        <div className="absolute bottom-patreon-4 left-patreon-4 opacity-20">
          <div className="w-12 h-12 bg-patreon-white/20 rounded-full blur-lg"></div>
        </div>

        {/* Creator info overlay */}
        <div className="absolute bottom-patreon-4 left-patreon-6 right-patreon-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="bg-patreon-white/90 backdrop-blur-sm px-patreon-3 py-patreon-1 rounded-full">
                <span className="text-patreon-xs font-patreon-medium text-patreon-gray">
                  {creator.category}
                </span>
              </div>
            </div>

            <button
              onClick={handleLike}
              className="p-patreon-2 bg-patreon-white/90 backdrop-blur-sm rounded-full hover:bg-patreon-white transition-colors duration-patreon-fast group/btn"
            >
              {isLiked ? (
                <HeartSolidIcon className="w-5 h-5 text-patreon-orange group-hover/btn:scale-110 transition-transform duration-patreon-fast" />
              ) : (
                <HeartOutlineIcon className="w-5 h-5 text-patreon-gray group-hover/btn:text-patreon-orange transition-colors duration-patreon-fast" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div className="p-patreon-6">
        {/* Creator Info */}
        <div className="flex items-start justify-between mb-patreon-4">
          <div className="flex items-center flex-1 min-w-0">
            <div className="w-12 h-12 bg-patreon-gray-lighter rounded-full mr-patreon-3 flex-shrink-0 relative overflow-hidden">
              {creator.avatar ? (
                <img
                  src={creator.avatar}
                  alt={creator.name}
                  className="w-full h-full rounded-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-patreon-orange to-patreon-pink flex items-center justify-center text-patreon-white font-patreon-bold text-patreon-lg">
                  {creator.name.charAt(0)}
                </div>
              )}
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="font-patreon-semibold text-patreon-black text-patreon-lg truncate group-hover:text-patreon-orange transition-colors duration-patreon-fast">
                {creator.name}
              </h3>
              <p className="text-patreon-gray text-patreon-sm">
                {formatSupporters(creator.supporters)} supporters
              </p>
            </div>
          </div>

          {/* Verified Badge */}
          {creator.supporters > 10000 && (
            <div className="flex items-center ml-patreon-2">
              <div className="w-6 h-6 bg-patreon-success rounded-full flex items-center justify-center">
                <SparklesIcon className="w-3 h-3 text-patreon-white" />
              </div>
            </div>
          )}
        </div>

        {/* Description */}
        <p className="text-patreon-gray text-patreon-sm leading-patreon-relaxed mb-patreon-6 line-clamp-3 group-hover:line-clamp-none transition-all duration-patreon-normal">
          {creator.description}
        </p>

        {/* Action Buttons */}
        <div className="flex items-center justify-between">
          <button
            onClick={handleBecomePatron}
            className={`flex-1 mr-patreon-3 py-patreon-3 font-patreon-medium rounded-patreon-lg transition-all duration-patreon-normal ${
              isHovered
                ? "text-patreon-white shadow-patreon-md transform -translate-y-patreon-1 patreon-primary-gradient"
                : "bg-patreon-gray-lightest text-patreon-gray hover:bg-patreon-gray-lighter"
            }`}
            style={isHovered ? { background: 'var(--patreon-btn-primary-bg)', boxShadow: 'var(--patreon-btn-primary-shadow)' } : undefined}
          >
            {isHovered ? (
              <span className="flex items-center justify-center">
                Become a patron
                <PlayIcon className="w-4 h-4 ml-patreon-2" />
              </span>
            ) : (
              "Support"
            )}
          </button>

          <button
            className="px-patreon-4 py-patreon-3 bg-patreon-white border border-patreon-gray-lighter text-patreon-gray rounded-patreon-lg hover:border-patreon-orange hover:text-patreon-orange transition-all duration-patreon-fast"
          >
            <PlayIcon className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Hover overlay for featured cards */}
      {variant === "featured" && (
        <motion.div
          initial={{ opacity: 0 }}
          whileHover={{ opacity: 1 }}
          className="absolute inset-0 bg-gradient-to-t from-patreon-black/60 via-patreon-black/20 to-transparent pointer-events-none"
        />
      )}
    </motion.div>
  );
};

export default PatreonCreatorCard;

// Featured version for hero section
export const PatreonFeaturedCreatorCard: React.FC<Omit<PatreonCreatorCardProps, "variant">> = (props) => (
  <PatreonCreatorCard {...props} variant="featured" />
);

// Compact version for lists
export const PatreonCompactCreatorCard: React.FC<Omit<PatreonCreatorCardProps, "variant">> = (props) => (
  <PatreonCreatorCard {...props} variant="compact" />
);

